/**
 * OTL pre-order — PURE logic. No DOM, no network, no side effects.
 *
 * This file ships to the theme verbatim AND is unit-tested by evaluating these
 * exact bytes (see lib/preorder/preorderCore.test.js). There is no second copy
 * to drift out of sync — the tests exercise what the browser actually runs.
 *
 * THE RULE (locked by the operator):
 *   - Shopify variant inventory is the source of truth. Negative = units pre-ordered.
 *   - A piece is a PRE-ORDER piece when it doesn't exist yet, i.e. it's ordered
 *     beyond what's on hand. In-stock pieces NEVER count toward the 100.
 *   - Pre-order minimum is 100 pieces per style + color (NOT per size). Sizes
 *     within one color add up together.
 *
 * WHY WE READ STRUCTURED OPTIONS, NOT VARIANT TITLES (verified 2026-07-15):
 * The backend's extractColor() splits the variant title on "/". That is wrong
 * here: 26 real color values CONTAIN a slash ("Black Mesh/Black"), so parsing
 * "Black Mesh/Black / S" yields "Black Mesh / Black" — a string that matches no
 * real option value. The theme is handed structured options, so we use them:
 * 98 of 114 products name option 1 "Color"; the 16 that don't are untracked
 * service/draft items (Pattern, Sample, Labels...) that can never be pre-orders.
 */
var OTLPreorderCore = (function () {
  'use strict';

  /** Pieces required per style+color before a pre-order group may check out. */
  var DEFAULT_MINIMUM = 100;

  /** Line property our add-to-cart split stamps on pre-order pieces. */
  var PREORDER_PROPERTY = '_preorder';

  /**
   * Globo's equivalent property. We READ it during cutover so a cart built by
   * the old app still groups correctly; we never WRITE it. (Phase 3's backend
   * detection accepts both for the same reason.)
   */
  var GLOBO_PROPERTY = '_is_preorder';

  /**
   * Marks a line added by the storefront's SAMPLE box (1 piece, "let me feel the
   * fabric before I commit to 100").
   *
   * ⚠️ Samples are a DELIBERATE, wanted flow (operator, 2026-07-16: "customers can
   * order a sample but only 1") and they must NEVER be caught by the 100pc minimum —
   * requiring 100 samples defeats the entire point of a sample.
   *
   * The marker exists because a sample is otherwise **indistinguishable** from a
   * cart-permalink bypass: both are a line with no `_preorder` property, quantity 1,
   * on a zero-stock variant. That bypass is exactly what Phase 5's arithmetic
   * backstop is there to catch — so without an explicit marker, the backstop cannot
   * tell "legitimate sample" from "someone routing around the theme", and would have
   * to block both. Hence: the sample box stamps this, and everything downstream
   * exempts it.
   */
  var SAMPLE_PROPERTY = '_sample';

  /** Option name that carries colour. Matches "Color" and "Colour", any case. */
  var COLOR_OPTION = /^colou?r$/i;

  function toInt(n) {
    var v = parseInt(n, 10);
    return isNaN(v) ? 0 : v;
  }

  /**
   * Style number = the leading digits of the product title ("6031 - Women's..." → "6031").
   * Service items ("Pattern", "Sample") have no style number → null, and the
   * caller falls back to the product title for display.
   */
  function styleNumberFromTitle(title) {
    if (!title || typeof title !== 'string') return null;
    var m = title.match(/^\s*(\d{3,})/);
    return m ? m[1] : null;
  }

  /**
   * The STYLE CODE exactly as the operator says it: "3008", "7003HW", "OVSAM1".
   *
   * Real titles are "<CODE> - <description>". Taking the leading digits alone is
   * wrong: "7003HW - Unisex Heavyweight Fleece Crewneck Sweatshirt" would give
   * "7003" and orphan the "HW", which is part of the code (heavyweight vs "7003MW"
   * midweight — two different products). So: everything before the first " - ".
   *
   * Falls back to leading digits+letters, then null (service items like "Sample"
   * and "Label removal (per piece)" have no code at all).
   */
  function styleCodeFromTitle(title) {
    if (!title || typeof title !== 'string') return null;
    // [^-–—]+? stops at the first dash; the \s+ requires it to be a real separator,
    // so a hyphen inside a word ("One-Piece") is never mistaken for one.
    var m = title.match(/^\s*([^-–—]+?)\s*[-–—]\s+/);
    if (m && m[1].trim()) return m[1].trim();
    var d = title.match(/^\s*(\d{3,}[A-Za-z]*)/);
    return d ? d[1] : null;
  }

  /**
   * Short label for a group: "Style #7003HW".
   *
   * Deliberately does NOT include the description. It used to read
   * "Style #7003 HW - Unisex Heavyweight Fleece Crewneck Sweatshirt", which the
   * operator called too wordy — and he's right: the customer is looking at the
   * product, so the full name adds nothing and buries the number that matters.
   */
  function styleLabel(productTitle) {
    var code = styleCodeFromTitle(productTitle);
    if (!code) return productTitle || 'This item';
    return 'Style #' + code;
  }

  /**
   * The colour of a line/variant, read from structured options.
   * `optionsWithValues` is [{name, value}] — present on cart lines (cart.js) and
   * buildable on the product page from product.options + variant.options.
   * Returns null when the product has no colour option (service items), which
   * callers treat as "one group for the whole product".
   */
  function colorOf(optionsWithValues) {
    if (!optionsWithValues || !optionsWithValues.length) return null;
    for (var i = 0; i < optionsWithValues.length; i++) {
      var o = optionsWithValues[i];
      if (o && COLOR_OPTION.test(String(o.name || ''))) {
        var v = o.value == null ? '' : String(o.value).trim();
        return v === '' ? null : v;
      }
    }
    return null;
  }

  /**
   * Pair product.options (names) with variant.options (values) into [{name,value}].
   * The product-page shape; cart.js already gives options_with_values directly.
   */
  function zipOptions(productOptionNames, variantOptionValues) {
    var out = [];
    if (!productOptionNames || !variantOptionValues) return out;
    for (var i = 0; i < productOptionNames.length; i++) {
      var name = productOptionNames[i];
      // product.options may be [{name},...] or ["Color",...] depending on source.
      if (name && typeof name === 'object') name = name.name;
      out.push({ name: name, value: variantOptionValues[i] });
    }
    return out;
  }

  /** Stable grouping key. Product id + colour — never the parsed title. */
  function groupKeyFor(productId, color) {
    return String(productId) + '::' + (color == null ? '' : color);
  }

  /**
   * Can this variant be pre-ordered AT ALL? Two conditions, both from Shopify:
   *
   *   tracked  — an untracked variant has no count to exceed, so "beyond stock"
   *              is meaningless. Shopify never blocks it. Never a pre-order.
   *   CONTINUE — the policy that permits overselling. Under DENY, Shopify REFUSES
   *              the sale at 0. Promising a pre-order on a DENY variant would be a
   *              lie the cart then rejects.
   *
   * This is the ONE definition of "pre-orderable" — display, the add-to-cart split
   * and the overflow prompt all go through it, so they can't disagree.
   *
   * ⚠️ Live-data note (2026-07-15): style 6031 is TRACKED + DENY right now, and the
   * flip to CONTINUE is deliberately deferred to the Phase 5 cutover. So until that
   * runs, 6031 correctly still reads "Sold Out" — that is not a bug, it is this
   * function telling the truth about what Shopify will actually accept today.
   */
  function isPreorderable(variant) {
    if (!variant) return false;
    var tracked = variant.inventory_management != null && variant.inventory_management !== '';
    if (!tracked) return false;
    return String(variant.inventory_policy || '').toLowerCase() === 'continue';
  }

  /**
   * Split a requested quantity into in-stock pieces and pre-order pieces.
   *
   * `variant` needs: inventory_quantity, inventory_management, inventory_policy
   * (all three are present on /products/<handle>.js).
   *
   * For a pre-orderable variant this mirrors the Phase 5 checkout function exactly:
   *   preorderUnits = max(0, requested − max(0, available))
   * For anything else, every piece is treated as in-stock — we never mint a
   * pre-order line Shopify would reject.
   */
  /**
   * Units of `variantId` in `items` that were counted against STOCK — unstamped lines
   * only. Stamped (`_preorder`, or Globo's `_is_preorder`) units are by definition
   * beyond stock and consumed none, so counting them would over-count and push
   * genuinely in-stock pieces into the pre-order bucket.
   *
   * Lives in core, not in the cart script, so it is reachable by the unit suite. The
   * first version of this sat in otl-preorder-cart.js and called
   * `lineIsPreorder(line)` instead of `lineIsPreorder(line.properties)` — the
   * predicate takes a PROPERTIES BAG, so it silently returned false for every line
   * and the skip was dead code. Nothing caught it, because a unit test of
   * splitQuantity cannot see what its caller passes.
   */
  function claimedStockFor(items, variantId) {
    if (!items || !items.length || variantId == null) return 0;
    var id = String(variantId);
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!line || String(line.variant_id) !== id) continue;
      if (lineIsPreorder(line.properties)) continue; // beyond stock already
      total += toInt(line.quantity);
    }
    return total > 0 ? total : 0;
  }

  /**
   * @param claimed units of this variant ALREADY in the cart against stock (optional).
   *
   * Without it the split is cart-blind: it asks "how much stock exists", never "how
   * much is left". Add 28 to a 27-stock variant twice and you got 27+1 then 27+1
   * again — 54 unstamped pieces on 27 of stock, with the rule seeing 2 pre-order
   * pieces instead of 29. Verified live before the fix.
   *
   * Defaults to 0, which is exactly the old behaviour, so every existing caller and
   * test keeps its meaning.
   */
  function splitQuantity(variant, requested, claimed) {
    var qty = toInt(requested);
    if (qty <= 0) return { inStock: 0, preorder: 0 };
    if (!isPreorderable(variant)) return { inStock: qty, preorder: 0 };

    var available = toInt(variant.inventory_quantity);
    if (available < 0) available = 0; // already oversold — every new piece is pre-order
    available = Math.max(0, available - Math.max(0, toInt(claimed)));
    var inStock = Math.min(qty, available);
    return { inStock: inStock, preorder: qty - inStock };
  }

  /** Read one property off either shape cart surfaces use (object, or [[k,v],...]). */
  function propOf(properties, key) {
    if (!properties) return undefined;
    if (Array.isArray(properties)) {
      for (var i = 0; i < properties.length; i++) {
        if (properties[i] && properties[i][0] === key) return properties[i][1];
      }
      return undefined;
    }
    return properties[key];
  }

  /** True if this line is a 1-piece sample, which the 100pc rule must never touch. */
  function lineIsSample(properties) {
    return Boolean(propOf(properties, SAMPLE_PROPERTY));
  }

  /** True if this cart line was stamped as pre-order (ours, or Globo's during cutover). */
  function lineIsPreorder(properties) {
    if (!properties) return false;
    // cart.js gives an object; some surfaces give [[k,v],...].
    if (Array.isArray(properties)) {
      for (var i = 0; i < properties.length; i++) {
        var k = properties[i] && properties[i][0];
        if (k === PREORDER_PROPERTY || k === GLOBO_PROPERTY) return true;
      }
      return false;
    }
    return Boolean(properties[PREORDER_PROPERTY] || properties[GLOBO_PROPERTY]);
  }

  /**
   * Group a cart's pre-order lines by style+colour and report each group's shortfall.
   *
   * Only lines carrying the pre-order property count — an in-stock line sitting in
   * the same cart contributes nothing, which is the "in-stock never counts toward
   * the 100" rule made structural.
   *
   * @param {Array} items  cart.js `items`
   * @param {number} [minimum]
   * @returns {Array<{key,styleLabel,color,units,shortfall,url,lines,productId}>}
   *          sorted by key for stable rendering; only groups with 0 < units < minimum
   *          are "short" — that's `shortfall > 0`.
   */
  function groupPreorderLines(items, minimum) {
    var min = toInt(minimum) || DEFAULT_MINIMUM;
    var groups = {};
    var order = [];

    (items || []).forEach(function (item) {
      if (!item || !lineIsPreorder(item.properties)) return;
      // A sample can never count toward — or be blocked by — the minimum.
      if (lineIsSample(item.properties)) return;
      var color = colorOf(item.options_with_values);
      var key = groupKeyFor(item.product_id, color);
      if (!groups[key]) {
        groups[key] = {
          key: key,
          productId: item.product_id,
          // The run builder needs to fetch /products/<handle>.js to learn the OTHER
          // sizes in this colour — the cart only knows the ones already added.
          handle: item.handle || null,
          productTitle: item.product_title || null,
          styleLabel: styleLabel(item.product_title),
          color: color,
          units: 0,
          url: item.url || null,
          lines: [],
        };
        order.push(key);
      }
      groups[key].units += toInt(item.quantity);
      groups[key].lines.push(item);
    });

    return order.sort().map(function (k) {
      var g = groups[k];
      g.shortfall = g.units > 0 && g.units < min ? min - g.units : 0;
      return g;
    });
  }

  /** Groups that block checkout. Empty ⇒ the cart may proceed. */
  function blockingGroups(items, minimum) {
    return groupPreorderLines(items, minimum).filter(function (g) {
      return g.shortfall > 0;
    });
  }

  /**
   * The operator's own wording (2026-07-16), after he walked the real flow and
   * called the first version too wordy:
   *   "Style #7003HW in White only has 14 pieces in cart but 100pc is required —
   *    you need to add 86 more"
   * "in White" rather than "color White"; no product description.
   */
  function shortfallMessage(group, minimum) {
    var min = toInt(minimum) || DEFAULT_MINIMUM;
    var colorPart = group.color ? ' in ' + group.color : '';
    var piece = group.units === 1 ? 'piece' : 'pieces';
    return group.styleLabel + colorPart + ' only has ' + group.units + ' ' + piece +
      ' in cart but ' + min + 'pc is required — you need to add ' + group.shortfall + ' more';
  }

  /** Short, positive form once a group is satisfied: "Style #7003HW in White — 100 of 100 ✓". */
  function readyMessage(group, minimum) {
    var min = toInt(minimum) || DEFAULT_MINIMUM;
    var colorPart = group.color ? ' in ' + group.color : '';
    return group.styleLabel + colorPart + ' — ' + group.units + ' of ' + min + ' pieces';
  }


  /**
   * The SIZE of a line/variant: the first structured option that isn't colour.
   *
   * Was inlined in the cart modal's row renderer, where no test could reach it.
   * Moved here after D9's lesson — logic the harness can't see is untested logic,
   * however green the suite looks.
   */
  function sizeOf(optionsWithValues) {
    if (!optionsWithValues || !optionsWithValues.length) return null;
    for (var i = 0; i < optionsWithValues.length; i++) {
      var o = optionsWithValues[i];
      if (o && !COLOR_OPTION.test(String(o.name || ''))) {
        var v = o.value == null ? '' : String(o.value).trim();
        if (v !== '') return v;
      }
    }
    return null;
  }

  /**
   * Progress of ONE style+colour run toward the minimum. The single source of
   * truth for the bar, which paints in four places (product page, run builder,
   * cart page, drawer).
   *
   * ⚠️ `units` counts PRE-ORDER pieces only — never in-stock pieces. That is the
   * operator's locked rule ("in-stock pieces NEVER count toward the 100"), and it
   * is the reason the bar can sit still while a stepper moves: stepping a size
   * that still has stock buys pieces that ship now, and those are not part of the
   * run. The approved Tier-3 mockup counted every stepped piece, which would let a
   * group reach 100 on ~73 real made-to-order pieces — the exact failure the rule
   * exists to prevent. The UI explains the difference per size instead.
   */
  function runProgress(group, minimum) {
    var min = toInt(minimum) || DEFAULT_MINIMUM;
    var units = group ? toInt(group.units) : 0;
    if (units < 0) units = 0;
    var met = units >= min;
    return {
      units: units,
      minimum: min,
      remaining: met ? 0 : min - units,
      met: met,
      // Clamped both ends: a >100 run must not overflow the bar, and min is never
      // 0 here (toInt(minimum) || DEFAULT_MINIMUM), so there is no divide-by-zero.
      percent: Math.max(0, Math.min(100, Math.round((units / min) * 100))),
    };
  }

  /**
   * Heading for the run builder — the operator's approved wording, which replaced
   * "Pre-order minimum not met". That old heading sat directly above a green tick
   * reading "100 of 100 pieces" whenever the customer fixed the shortfall, so the
   * modal contradicted itself at the exact moment it should have said "done".
   */
  function runHeading(group) {
    if (group && group.color) return 'Building your ' + group.color + ' run';
    var label = group && group.styleLabel ? group.styleLabel : null;
    return label ? 'Building your ' + label + ' run' : 'Building your run';
  }

  /** One line under the bar: what to do next, or that there is nothing left to do. */
  function runStatusMessage(group, minimum) {
    var p = runProgress(group, minimum);
    if (p.met) return 'Minimum met — ready to check out';
    return p.remaining + ' more to go — add any sizes below';
  }

  /**
   * Every variant of `product` in `color`, in the product's own option order, each
   * paired with the pre-order units already in `items` and its on-hand count.
   *
   * This is what turns the old one-size-at-a-time modal into the run builder. It is
   * the right shape for how customers actually buy: across 2000 real orders, 78 of
   * the 79 groups that reached 100+ spanned more than one size.
   *
   * Excludes variants that cannot be pre-ordered at all (untracked, or DENY — see
   * isPreorderable), because offering a run slot Shopify would refuse at checkout
   * is a promise we cannot keep.
   */
  function runBuilderRows(product, items, color) {
    if (!product || !product.variants) return [];
    var names = (product.options || []).map(function (o) { return (o && o.name) ? o.name : o; });
    var rows = [];
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var opts = zipOptions(names, v.options || []);
      var c = colorOf(opts);
      // Null colour matches null colour — a product with no colour option is one
      // single group, and its rows must still be reachable from the builder.
      if ((c == null ? '' : c) !== (color == null ? '' : color)) continue;
      if (!isPreorderable(v)) continue;

      var pre = preorderLineFor(items, v.id);
      var stock = stockLineFor(items, v.id);
      var available = toInt(v.inventory_quantity);
      if (available < 0) available = 0;
      var preUnits = pre ? toInt(pre.quantity) : 0;
      var stockUnits = stock ? toInt(stock.quantity) : 0;

      rows.push({
        variantId: v.id,
        size: sizeOf(opts) || v.title || '',
        /**
         * A size can hold TWO lines — the pieces that ship now and the pieces we
         * make. The stepper sets the size's TOTAL and the write splits it, so the
         * builder needs both keys, not just the pre-order one.
         */
        preorderKey: pre ? pre.key : null,
        stockKey: stock ? stock.key : null,
        preorderUnits: preUnits,
        stockUnits: stockUnits,
        total: preUnits + stockUnits,
        available: available,
      });
    }
    return rows;
  }

  /**
   * The ONE unstamped ("ships now") cart line for a variant, or null.
   *
   * Mirror of preorderLineFor. A sample is excluded from both: it is its own thing,
   * exempt from the minimum, and the builder must never silently fold a customer's
   * sample into a run's ship-now bucket.
   */
  function stockLineFor(items, variantId) {
    if (!items || !items.length || variantId == null) return null;
    var id = String(variantId);
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!line || String(line.variant_id) !== id) continue;
      if (lineIsPreorder(line.properties)) continue;
      if (lineIsSample(line.properties)) continue;
      return line;
    }
    return null;
  }

  /**
   * Split a size's requested TOTAL into pieces that ship now and pieces we make.
   *
   * The builder's stepper sets a total, not a made-to-order count. Without this the
   * builder stamped every stepped piece as pre-order, so a customer stepping a size
   * with 60 on the shelf to 100 filled the bar to 100/100 and production was asked
   * for 100 garments while 60 sat in the rack. That is exactly the failure the
   * operator's "in-stock pieces NEVER count toward the 100" rule exists to prevent,
   * and the version before this one broke it while claiming in a comment that it
   * did not.
   */
  function splitRunTotal(total, available) {
    var t = toInt(total);
    if (t <= 0) return { shipNow: 0, made: 0 };
    var a = toInt(available);
    if (a < 0) a = 0;
    var shipNow = Math.min(t, a);
    return { shipNow: shipNow, made: t - shipNow };
  }

  /**
   * The ONE pre-order cart line for a variant, or null.
   *
   * Deliberately returns the first match rather than summing: the builder edits a
   * line by its `key`, and Shopify merges adds with identical properties, so a
   * second stamped line for the same variant should not exist. If one ever does
   * (a Globo-stamped line alongside ours during cutover), editing the first and
   * letting the next cart read re-group is safer than inventing a merged key that
   * addresses nothing.
   */
  function preorderLineFor(items, variantId) {
    if (!items || !items.length || variantId == null) return null;
    var id = String(variantId);
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!line || String(line.variant_id) !== id) continue;
      if (!lineIsPreorder(line.properties)) continue;
      if (lineIsSample(line.properties)) continue;
      return line;
    }
    return null;
  }

  /**
   * How a cart line should be labelled to the customer (D10).
   *
   * Two lines for the same variant at the same price, with identical titles, is
   * what the customer sees today after a split — one that ships now and one that
   * does not, and nothing on screen tells them apart.
   *
   * Samples are labelled as samples, not as pre-orders: a sample is exempt from the
   * minimum, so calling it "Pre-order" would imply it counts toward a run it can
   * never contribute to.
   */
  function lineLabel(line, leadTime) {
    var props = line ? line.properties : null;
    if (lineIsSample(props)) return 'Sample';
    if (lineIsPreorder(props)) {
      return leadTime ? 'Pre-order \u00b7 ' + leadTime : 'Pre-order';
    }
    return 'Ships now';
  }


  /**
   * The property bag that marks a line as pre-order. ONE definition, because two
   * places now create stamped lines — the product page's add-to-cart split and the
   * run builder's steppers — and a stamp that differs by so much as a key would
   * split one run into two groups that each look short.
   *
   * `_style` / `_color` make the group legible to the cart UI and to Phase 3's
   * order tagging without re-deriving them from the title.
   */
  function preorderProperties(productTitle, color) {
    var props = {};
    props[PREORDER_PROPERTY] = 'yes';
    props._style = styleNumberFromTitle(productTitle) || productTitle || '';
    if (color) props._color = color;
    return props;
  }

  /**
   * The progress bar's MARKUP. Pure — it builds a string and touches no DOM, no
   * network and no globals, so the unit suite can assert on it directly.
   *
   * It lives here rather than in the cart script because the bar paints in four
   * places (product page, run builder, cart page, drawer) across two files, and a
   * second copy is exactly how the badge came to say 28 while the drawer said 1.
   */
  function runProgressHtml(group, minimum, opts) {
    var o = opts || {};
    var p = runProgress(group, minimum);
    var label = (group && group.styleLabel ? group.styleLabel : 'This style') +
      (group && group.color ? ' \u00b7 ' + group.color : '');
    var status = o.status === false ? '' :
      '<p class="otl-run__status">' + escapeHtml(runStatusMessage(group, minimum)) + '</p>';
    return '<div class="otl-run' + (p.met ? ' is-met' : '') + '" data-otl-run>' +
        '<div class="otl-run__head">' +
          '<span class="otl-run__label">' + escapeHtml(label) + '</span>' +
          '<span class="otl-run__count" data-otl-run-count>' + p.units + ' / ' + p.minimum + ' pc</span>' +
        '</div>' +
        '<div class="otl-run__track" role="progressbar" aria-valuemin="0" aria-valuemax="' + p.minimum +
          '" aria-valuenow="' + p.units + '" aria-label="' + escapeHtml(label) + ' run progress">' +
          '<span class="otl-run__fill" data-otl-run-fill style="width:' + p.percent + '%"></span>' +
        '</div>' +
        status +
      '</div>';
  }

  /**
   * HTML-escape. Named `escapeHtml` here (the consumers each had their own `esc`)
   * so the markup builders above cannot accidentally interpolate a raw colour like
   * `Black "Mesh"` — real option values contain quotes and slashes.
   */
  function escapeHtml(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* OTL-CORE-EXPORTS — lib/preorder/themeAssets.test.js anchors on this marker to
     check that every core.x the theme's other two assets call is actually exported.
     Keep it immediately above the return, or that test will tell you it is gone. */
  return {
    DEFAULT_MINIMUM: DEFAULT_MINIMUM,
    PREORDER_PROPERTY: PREORDER_PROPERTY,
    SAMPLE_PROPERTY: SAMPLE_PROPERTY,
    GLOBO_PROPERTY: GLOBO_PROPERTY,
    styleNumberFromTitle: styleNumberFromTitle,
    styleCodeFromTitle: styleCodeFromTitle,
    styleLabel: styleLabel,
    colorOf: colorOf,
    zipOptions: zipOptions,
    groupKeyFor: groupKeyFor,
    isPreorderable: isPreorderable,
    splitQuantity: splitQuantity,
    claimedStockFor: claimedStockFor,
    lineIsPreorder: lineIsPreorder,
    lineIsSample: lineIsSample,
    groupPreorderLines: groupPreorderLines,
    blockingGroups: blockingGroups,
    shortfallMessage: shortfallMessage,
    readyMessage: readyMessage,
    sizeOf: sizeOf,
    runProgress: runProgress,
    runHeading: runHeading,
    runStatusMessage: runStatusMessage,
    runBuilderRows: runBuilderRows,
    preorderLineFor: preorderLineFor,
    stockLineFor: stockLineFor,
    splitRunTotal: splitRunTotal,
    lineLabel: lineLabel,
    preorderProperties: preorderProperties,
    runProgressHtml: runProgressHtml,
    escapeHtml: escapeHtml,
  };
})();

// Node/vitest interop — harmless in the browser, where `module` is undefined.
if (typeof module !== 'undefined' && module.exports) module.exports = OTLPreorderCore;
